import { useState, useEffect, useRef, type ReactNode } from "react";
import { usePageTitle } from "@/hooks/usePageTitle";
import AddToOfferingDialog from "@/components/teacher/classes/AddToOfferingDialog";
import { useSearchParams, Link } from "react-router-dom";
import {
  Send, Mail, Archive, AlertTriangle,
  MessageCircle, Calendar, MessageSquare, CheckCheck, Check,
  RefreshCw, HelpCircle, Shield, Undo2, Coins, ArrowLeft, Search,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { SidebarProvider } from "@/components/ui/sidebar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DashboardTopNav } from "@/components/layout/DashboardTopNav";
import { TeacherSidebar } from "@/components/teacher/dashboard/TeacherSidebar";
import { toast } from "@/hooks/use-toast";
import {
  useTeacherInquiries, useMessages, useSendTeacherMessage, useMarkTeacherAsRead,
} from "@/hooks/use-inquiries";
import { useCurrentUser } from "@/hooks/use-current-user";
import { useAuth } from "@/hooks/use-auth-compat";
import { useTeacherProfile } from "@/contexts/TeacherProfileContext";
import { useSubscriptionStatus } from "@/hooks/use-teacher-subscription";
import { fetchTeacherCoinSummary } from "@/services/teacherProfileService";
import { isReleaseFlagOn } from "@/config/featureFlags";
import { cn } from "@/lib/utils";
import type { InquiryThread } from "@/services/inquiryService";
import { LeadBandChip } from "@/components/inquiries/LeadBandChip";

function getGuaranteeNote(state: InquiryThread["guaranteeState"]): {
  text: string; icon: typeof Shield; className: string;
} | null {
  switch (state) {
    case "HELD":      return { text: "Protected by money-back guarantee", icon: Shield, className: "border-sky-200 bg-sky-50 text-sky-700" };
    case "REFUNDED":  return { text: "Coins returned — lead didn't qualify", icon: Undo2, className: "border-emerald-200 bg-emerald-50 text-emerald-700" };
    case "KEPT_OVER_CAP": return { text: "Refund cap reached this period", icon: Shield, className: "border-slate-300 bg-slate-100 text-slate-600" };
    default: return null;
  }
}

function getInitials(name: string): string {
  return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
}

function avatarStyle(name: string) {
  const palettes = [
    { bg: "bg-indigo-500", text: "text-white" },
    { bg: "bg-violet-500", text: "text-white" },
    { bg: "bg-amber-500",   text: "text-white" },
    { bg: "bg-rose-500",   text: "text-white" },
    { bg: "bg-amber-500",  text: "text-white" },
    { bg: "bg-sky-500",    text: "text-white" },
    { bg: "bg-emerald-500",text: "text-white" },
    { bg: "bg-fuchsia-500",text: "text-white" },
  ];
  return palettes[(name.charCodeAt(0) || 0) % palettes.length];
}

function formatTimeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

function formatTime(dateStr: string): string {
  const date = new Date(dateStr);
  const today = new Date();
  const isToday = date.toDateString() === today.toDateString();
  if (isToday) return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return date.toLocaleDateString([], { month: "short", day: "numeric" }) +
    " · " + date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function formatTierLabel(tier: string): string {
  return tier.charAt(0) + tier.slice(1).toLowerCase();
}

type TabValue = "all" | "ACTIVE" | "ARCHIVED";

export default function TeacherInquiriesPage() {
  usePageTitle('Student Inquiries | BookMyCoaching');
  const [searchParams] = useSearchParams();
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(searchParams.get("thread"));
  const [activeTab, setActiveTab] = useState<TabValue>("all");
  const [replyText, setReplyText] = useState("");
  const [replyError, setReplyError] = useState<ReactNode | null>(null);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { user } = useCurrentUser();
  const { teacherProfile } = useTeacherProfile();
  const auth = useAuth();
  const token = auth.user?.access_token ?? "";

  const { data: coinSummary } = useQuery({
    queryKey: ["teacherCoinSummary"],
    queryFn: () => fetchTeacherCoinSummary(token),
    enabled: !!token,
  });
  const isBelowRechargeThreshold = coinSummary?.balance != null && coinSummary.balance < coinSummary.chargeAmount;
  const { data: subscriptionStatus } = useSubscriptionStatus();
  const tierLabel = formatTierLabel(subscriptionStatus?.subscription?.tier ?? "SILVER");

  const statusFilter = activeTab === "all" ? undefined : activeTab;
  const { data, isLoading, isError, refetch } = useTeacherInquiries(0, 50, statusFilter);
  const { data: messagesData } = useMessages(selectedThreadId ?? undefined);
  const sendMessage = useSendTeacherMessage(selectedThreadId ?? "");
  const markAsRead = useMarkTeacherAsRead(selectedThreadId ?? "");

  const allThreads = data?.content ?? [];
  const threads = search.trim()
    ? allThreads.filter(t =>
        t.studentName?.toLowerCase().includes(search.toLowerCase()) ||
        t.subject?.toLowerCase().includes(search.toLowerCase())
      )
    : allThreads;

  const selectedThread = allThreads.find((t) => t.id === selectedThreadId) ?? null;
  const messages = messagesData?.content ?? [];
  const unreadCount = allThreads.filter((t) => t.unreadCount > 0).length;
  const activeCount = allThreads.filter(t => t.status === "ACTIVE").length;
  const selectedGuaranteeNote = selectedThread ? getGuaranteeNote(selectedThread.guaranteeState) : null;

  const teacherName = teacherProfile?.fullName || "";
  const teacherAvatar = teacherProfile?.profileImage || "";

  useEffect(() => {
    if (selectedThreadId) markAsRead.mutate();
  }, [selectedThreadId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [messages]);

  useEffect(() => {
    if (allThreads.length > 0 && !selectedThreadId) {
      setSelectedThreadId(allThreads[0].id);
    }
  }, [allThreads.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const preselected = searchParams.get("thread");
    if (preselected && allThreads.length > 0 && !allThreads.find(t => t.id === preselected)) {
      setActiveTab("all");
    }
  }, [allThreads.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSendReply = async () => {
    if (!replyText.trim() || !selectedThreadId) return;
    try {
      await sendMessage.mutateAsync(replyText.trim());
      setReplyText("");
      setReplyError(null);
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : "Failed to send";
      if (msg === "INSUFFICIENT_COINS") {
        setReplyError(
          <>You're out of BMC coins.{' '}
            <Link to="/teacher-coins" className="font-medium underline underline-offset-2 hover:text-destructive/80">Top up your coins</Link>{' '}
            to continue.</>
        );
      } else {
        toast({ title: "Error", description: msg, variant: "destructive" });
      }
    }
  };

  const tabs: { value: TabValue; label: string; count?: number }[] = [
    { value: "all",      label: "All",      count: allThreads.length },
    { value: "ACTIVE",   label: "Active",   count: activeCount },
    { value: "ARCHIVED", label: "Archived" },
  ];

  const [mobileShowConversation, setMobileShowConversation] = useState(false);

  const handleSelectThread = (id: string) => {
    setSelectedThreadId(id);
    setMobileShowConversation(true);
  };

  return (
    <div className="min-h-screen bg-white teacher-theme">
      <SidebarProvider>
        <DashboardTopNav role="teacher" />
        <div className="min-h-screen flex w-full pt-14">
          <TeacherSidebar teacherName={teacherName} teacherAvatar={teacherAvatar} />

          <div className="flex-1 flex flex-col min-w-0 overflow-hidden" style={{ height: "calc(100svh - 3.5rem)" }}>

            {/* ── Page header ── */}
            <div className={cn(
              "px-4 sm:px-6 pt-5 pb-4 border-b border-slate-100 shrink-0 flex items-center justify-between gap-4",
              mobileShowConversation && "hidden md:flex"
            )}>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-2xl font-bold text-slate-900">Inquiries</h1>
                  {unreadCount > 0 && (
                    <span className="inline-flex items-center text-xs font-bold px-2 py-0.5 rounded-full bg-indigo-600 text-white">
                      {unreadCount} new
                    </span>
                  )}
                </div>
                <p className="text-sm text-slate-400 mt-0.5">Respond to students interested in your offerings</p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <div className="text-right">
                  <p className="text-lg font-bold text-slate-800 leading-none">{activeCount}</p>
                  <p className="text-xs text-slate-400 mt-0.5">active</p>
                </div>
                <div className="w-px h-6 bg-slate-100" />
                <div className="text-right">
                  <p className="text-lg font-bold text-slate-800 leading-none">{allThreads.length}</p>
                  <p className="text-xs text-slate-400 mt-0.5">total</p>
                </div>
              </div>
            </div>

            {/* Low-balance warning */}
            {coinSummary && isBelowRechargeThreshold && (
              <div className={cn("px-4 sm:px-6 pt-3 shrink-0", mobileShowConversation && "hidden md:block")}>
                <div className="flex items-start gap-2.5 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3">
                  <AlertTriangle className="h-4 w-4 mt-0.5 text-amber-600 shrink-0" />
                  <p className="text-sm text-amber-800">
                    Your coin balance ({coinSummary.balance}) is below the {coinSummary.chargeAmount}-coin reply cost.{' '}
                    <Link to="/teacher-buy-coins" className="font-semibold underline underline-offset-2 hover:text-amber-950">Buy more coins</Link>{' '}
                    to respond.
                  </p>
                </div>
              </div>
            )}

            {/* ── Split panel ── */}
            <div className="flex-1 flex overflow-hidden">

              {/* Left: thread list */}
              <div className={cn(
                "flex flex-col border-r border-slate-100 bg-white overflow-hidden",
                "w-full md:w-72 lg:w-80 md:shrink-0",
                mobileShowConversation ? "hidden md:flex" : "flex"
              )}>
                {/* Search + tabs */}
                <div className="p-3 border-b border-slate-100 space-y-2 shrink-0">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Search students…"
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-300 placeholder:text-slate-400 transition-all"
                    />
                  </div>
                  <div className="flex gap-0.5">
                    {tabs.map((tab) => (
                      <button
                        key={tab.value}
                        onClick={() => { setActiveTab(tab.value); setSelectedThreadId(null); setMobileShowConversation(false); }}
                        className={cn(
                          "flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs font-semibold rounded-lg transition-all",
                          activeTab === tab.value
                            ? "bg-indigo-600 text-white shadow-sm"
                            : "text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                        )}
                      >
                        {tab.label}
                        {tab.count !== undefined && tab.count > 0 && (
                          <span className={cn(
                            "text-xs font-bold px-1.5 py-0.5 rounded-full leading-none",
                            activeTab === tab.value ? "bg-white/20 text-white" : "bg-slate-100 text-slate-500"
                          )}>
                            {tab.count}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Thread list */}
                <div className="flex-1 overflow-y-auto">
                  {isLoading ? (
                    <div role="status" aria-label="Loading inquiries" className="divide-y divide-slate-50">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="flex items-center gap-3 px-4 py-3.5 animate-pulse">
                          <div className="w-10 h-10 rounded-full bg-slate-100 shrink-0" />
                          <div className="flex-1 space-y-2">
                            <div className="h-3.5 bg-slate-100 rounded-full w-1/2" />
                            <div className="h-3 bg-slate-100 rounded-full w-3/4" />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : isError ? (
                    <div className="p-4">
                      <div className="flex items-start justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
                        <div className="flex items-start gap-2.5">
                          <AlertTriangle className="h-4 w-4 mt-0.5 text-amber-600 shrink-0" />
                          <p className="text-sm text-amber-800">Couldn't load your inbox. Please check your connection and try again.</p>
                        </div>
                        <button type="button" onClick={() => refetch?.()} className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-800 hover:opacity-80 shrink-0">
                          <RefreshCw className="w-3.5 h-3.5" /> Retry
                        </button>
                      </div>
                    </div>
                  ) : threads.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 px-4 text-center gap-3">
                      <div className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center">
                        <MessageCircle className="w-5 h-5 text-slate-400" />
                      </div>
                      <p className="text-sm text-slate-400">No inquiries yet</p>
                    </div>
                  ) : (
                    threads.map((thread) => {
                      const isUnread = thread.unreadCount > 0;
                      const isSelected = thread.id === selectedThreadId;
                      const style = avatarStyle(thread.studentName || "S");
                      return (
                        <button
                          key={thread.id}
                          onClick={() => handleSelectThread(thread.id)}
                          className={cn(
                            "w-full flex items-start gap-3 px-4 py-3.5 text-left transition-all border-b border-slate-50",
                            isSelected ? "bg-indigo-50" : "hover:bg-slate-50/80",
                          )}
                        >
                          <div className={cn(
                            "w-10 h-10 rounded-full flex items-center justify-center shrink-0 text-sm font-bold mt-0.5",
                            style.bg, style.text
                          )}>
                            {getInitials(thread.studentName || "S")}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2 mb-0.5">
                              <span className={cn("text-sm truncate", isUnread ? "font-bold text-slate-900" : "font-medium text-slate-700")}>
                                {thread.studentName}
                              </span>
                              <span className="text-xs text-slate-400 shrink-0">
                                {formatTimeAgo(thread.updatedAt)}
                              </span>
                            </div>
                            <p className={cn("text-xs truncate font-medium", isUnread ? "text-slate-700" : "text-slate-500")}>
                              {thread.subject}
                            </p>
                            {(thread.gradeBand || thread.curriculum || thread.modePreference || thread.startTimeline) && (
                              <p className="text-xs text-slate-400 truncate mt-0.5">
                                {[thread.gradeBand, thread.curriculum, thread.modePreference, thread.startTimeline]
                                  .filter(Boolean).join(" · ")}
                              </p>
                            )}
                            <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                              {thread.leadScoreBand !== undefined && (
                                <LeadBandChip band={thread.leadScoreBand ?? null} />
                              )}
                              {isUnread && (
                                <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-indigo-600 text-white">
                                  {thread.unreadCount} new
                                </span>
                              )}
                              {thread.status === "ARCHIVED" && (
                                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-400">
                                  Archived
                                </span>
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Right: conversation */}
              <div className={cn(
                "flex-1 flex flex-col min-w-0 bg-white overflow-hidden",
                mobileShowConversation ? "flex" : "hidden md:flex"
              )}>
                {!selectedThread ? (
                  <div className="flex-1 flex items-center justify-center">
                    <div className="text-center">
                      <div className="w-14 h-14 rounded-2xl bg-indigo-50 flex items-center justify-center mx-auto mb-3">
                        <MessageSquare className="w-6 h-6 text-indigo-400" />
                      </div>
                      <p className="text-sm font-semibold text-slate-700 mb-1">No conversation selected</p>
                      <p className="text-xs text-slate-400">Pick an inquiry from the list</p>
                    </div>
                  </div>
                ) : (
                  <div className="flex-1 flex flex-col min-h-0">

                    {/* Conversation header */}
                    <div className="border-b border-slate-100 bg-white shrink-0 px-4 sm:px-5 py-3.5">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          {/* Mobile back */}
                          <button
                            className="md:hidden p-1.5 -ml-1 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors"
                            onClick={() => setMobileShowConversation(false)}
                          >
                            <ArrowLeft className="w-4 h-4" />
                          </button>
                          {/* Avatar */}
                          {(() => {
                            const style = avatarStyle(selectedThread.studentName || "S");
                            return (
                              <div className={cn("w-9 h-9 rounded-full flex items-center justify-center shrink-0 text-sm font-bold", style.bg, style.text)}>
                                {getInitials(selectedThread.studentName || "S")}
                              </div>
                            );
                          })()}
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-bold text-slate-800 truncate">{selectedThread.studentName}</p>
                              {selectedThread.status === "ARCHIVED" ? (
                                <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 shrink-0">
                                  <Archive className="w-2.5 h-2.5" /> Archived
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 shrink-0">
                                  <CheckCheck className="w-2.5 h-2.5" /> Active
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-slate-400 truncate">{selectedThread.subject}</p>
                          </div>
                        </div>

                        {/* Class management: Add to Offering */}
                        {isReleaseFlagOn("CLASS_MANAGEMENT") && selectedThreadId && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="cursor-pointer transition-colors duration-150 shrink-0 text-xs"
                            onClick={() => setDialogOpen(true)}
                          >
                            Add to Offering
                          </Button>
                        )}

                        {/* Guarantee popover */}
                        {(selectedThread.leadScoreBand !== undefined || selectedGuaranteeNote) && (
                          <Popover>
                            <PopoverTrigger asChild>
                              <button type="button" aria-label="How the money-back guarantee works"
                                className="inline-flex items-center justify-center w-7 h-7 rounded-full text-slate-400 hover:bg-slate-100 transition-colors focus:outline-none shrink-0">
                                <HelpCircle className="w-4 h-4" />
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-80 text-sm">
                              <h3 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
                                <Shield className="w-4 h-4 text-sky-700" /> How the money-back guarantee works
                              </h3>
                              <ul className="space-y-1.5 text-slate-600 list-disc pl-4">
                                <li>A refund only happens if the student never replies to your first message.</li>
                                <li>Refunds are automatic once the follow-up window closes with no reply.</li>
                                <li>Cap: first 3 refunds every 30 days are free — after that, charges are kept.</li>
                              </ul>
                            </PopoverContent>
                          </Popover>
                        )}
                      </div>

                      {/* Lead band + reasons + guarantee note */}
                      {(selectedThread.leadScoreBand !== undefined || (selectedThread.leadScoreReasons?.length ?? 0) > 0 || selectedGuaranteeNote) && (
                        <div className="mt-2.5 space-y-2">
                          <div className="flex flex-wrap items-center gap-1.5">
                            {selectedThread.leadScoreBand !== undefined && <LeadBandChip band={selectedThread.leadScoreBand ?? null} />}
                            {(selectedThread.leadScoreReasons?.length ?? 0) > 0 && (
                              <ul role="list" className="flex flex-wrap items-center gap-1.5 list-none p-0 m-0">
                                {selectedThread.leadScoreReasons!.map((reason) => (
                                  <li key={reason} role="listitem" aria-label={reason}
                                    className="inline-flex items-center rounded-full bg-slate-100 text-slate-600 text-xs font-medium px-2.5 py-0.5">
                                    {reason}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                          {selectedGuaranteeNote && (
                            <div role="note" data-testid="guarantee-note" aria-label={selectedGuaranteeNote.text}
                              className={cn("flex items-center gap-2 rounded-lg border px-3 py-2 text-xs", selectedGuaranteeNote.className)}>
                              <selectedGuaranteeNote.icon className="w-3.5 h-3.5 shrink-0" />
                              <span>{selectedGuaranteeNote.text}</span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Email + availability */}
                      {(selectedThread.sharedEmail || selectedThread.preferredTimeSlots?.length > 0) && (
                        <div className="mt-2.5 pt-2.5 border-t border-slate-100 flex flex-col gap-2">
                          {selectedThread.sharedEmail && (
                            <div className="flex items-center gap-2 text-xs text-slate-500">
                              <Mail className="w-3.5 h-3.5 text-indigo-400 shrink-0" />
                              <span>Contact:</span>
                              <span className="font-semibold text-slate-700">{selectedThread.sharedEmail}</span>
                            </div>
                          )}
                          {selectedThread.preferredTimeSlots?.length > 0 && (
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="flex items-center gap-1 text-xs text-slate-400">
                                <Calendar className="w-3.5 h-3.5" /> Availability:
                              </span>
                              {selectedThread.preferredTimeSlots.map((s, i) => (
                                <span key={i} className="text-xs font-medium px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-700 border border-indigo-100">
                                  {s.date} · {s.timeSlot}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Messages */}
                    <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-5 space-y-3" style={{ background: "#f8f9fc" }}>
                      {selectedThread.guaranteeState === "REFUNDED" && (
                        <div role="status" data-testid="guarantee-refund-note" aria-label="Coins returned — lead didn't qualify"
                          className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
                          <Undo2 className="w-3.5 h-3.5 shrink-0" />
                          <span>Coins returned — lead didn't qualify</span>
                        </div>
                      )}
                      {messages.length === 0 && (
                        <div className="flex flex-col items-center justify-center h-full gap-2 py-16 text-center">
                          <div className="w-12 h-12 rounded-2xl bg-white border border-slate-200 flex items-center justify-center shadow-sm">
                            <MessageSquare className="w-5 h-5 text-slate-300" />
                          </div>
                          <p className="text-sm text-slate-400">No messages yet — say hello!</p>
                        </div>
                      )}
                      {messages.map((msg) => {
                        const isMine = msg.senderUserId === user?.id;
                        const studentStyle = avatarStyle(selectedThread.studentName || "S");
                        return (
                          <div key={msg.id} className={cn("flex gap-2", isMine ? "flex-row-reverse" : "flex-row")}>
                            <div className={cn(
                              "w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-xs font-bold self-end mb-4",
                              isMine ? "bg-indigo-600 text-white" : `${studentStyle.bg} ${studentStyle.text}`
                            )}>
                              {isMine ? getInitials(teacherName || "T") : getInitials(selectedThread.studentName || "S")}
                            </div>
                            <div className={cn("max-w-[70%] flex flex-col gap-1", isMine ? "items-end" : "items-start")}>
                              <div className={cn(
                                "px-4 py-2.5 text-sm leading-relaxed",
                                isMine
                                  ? "bg-indigo-600 text-white rounded-2xl rounded-br-md"
                                  : "bg-white border border-slate-200 text-slate-700 rounded-2xl rounded-bl-md shadow-sm"
                              )}>
                                {msg.content}
                              </div>
                              <div className={cn("flex items-center gap-1 px-1", isMine ? "flex-row-reverse" : "flex-row")}>
                                <p className="text-xs text-slate-400">{formatTime(msg.createdAt)}</p>
                                {isMine && (msg.seenAt
                                  ? <CheckCheck className="w-3 h-3 text-indigo-400" />
                                  : <Check className="w-3 h-3 text-slate-300" />
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      <div ref={messagesEndRef} />
                    </div>

                    {/* Reply area */}
                    {selectedThread.status === "ARCHIVED" ? (
                      <div className="mx-4 mb-4 py-3 px-4 bg-slate-50 rounded-xl border border-slate-200 text-center text-xs text-slate-400 flex items-center justify-center gap-2">
                        <Archive className="w-3.5 h-3.5" /> This conversation is archived.
                      </div>
                    ) : (
                      <div className="px-4 pb-4 pt-2 shrink-0 space-y-1.5">
                        {replyError && <p className="text-sm text-destructive px-1">{replyError}</p>}
                        {isReleaseFlagOn("TEACHER_TIER_BENEFITS") && coinSummary && (
                          <div className="flex items-center gap-1.5 px-1 text-xs text-slate-400">
                            <Coins className="w-3.5 h-3.5 shrink-0" />
                            {coinSummary.coinDiscountPct > 0 ? (
                              <span>
                                Reply costs{" "}
                                <span className="line-through text-slate-300">{coinSummary.standardChargeAmount}</span>{" "}
                                <span className="font-medium text-slate-600">{coinSummary.chargeAmount} coins</span>{" "}
                                ({tierLabel}, {coinSummary.coinDiscountPct}% off)
                              </span>
                            ) : (
                              <span>Reply costs {coinSummary.chargeAmount} coins</span>
                            )}
                          </div>
                        )}
                        <div className="flex items-end gap-2 bg-white rounded-2xl border border-slate-200 shadow-sm p-3">
                          <Textarea
                            placeholder="Write a reply…"
                            value={replyText}
                            onChange={(e) => { setReplyText(e.target.value); setReplyError(null); }}
                            rows={1}
                            className="flex-1 resize-none border-0 bg-transparent shadow-none focus-visible:ring-0 p-0 text-sm placeholder:text-slate-300 min-h-[36px] max-h-32"
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSendReply(); }
                            }}
                          />
                          <Button
                            onClick={handleSendReply}
                            disabled={!replyText.trim() || sendMessage.isPending}
                            aria-label="Send"
                            size="sm"
                            className="shrink-0 h-9 w-9 p-0 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40"
                          >
                            <Send className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                        <p className="text-xs text-slate-400 text-center">Press Enter to send · Shift+Enter for new line</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </SidebarProvider>

      {isReleaseFlagOn("CLASS_MANAGEMENT") && selectedThreadId && (
        <AddToOfferingDialog
          threadId={selectedThreadId}
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          studentName={selectedThread?.studentName}
          studentEmail={selectedThread?.sharedEmail}
        />
      )}
    </div>
  );
}
